import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Workbox } from 'workbox-window';

import { DEBUG } from '_lib/global/config';

/**
 * Pinning the URI to a service worker script served from the root of the web server.
 * If the custom hook is called from inside a router context and a relative path is
 * passed into the hook then the virtual path of the router is prepended to the service
 * worker URI which will absolutely cause a 404 response.
 * Pinning the service worker at the root means we cannot have nested service worker
 * scopes. YAGNI for now.
 */
const DEFAULT_URI = '/service-worker.js';

const useServiceWorker = () => {
  const workBox = useRef<Workbox | undefined>(undefined);
  // updated service worker script is installed and READY to be activated
  const [isUpdateAvailable, setIsUpdateAvailable] = useState<boolean>(false);
  // updated service worker script is currently being activated
  const [isUpdateInProgress, setIsUpdateInProgress] = useState<boolean>(false);

  // Register the service worker - opt in on first load
  useEffect(() => {
    if (!workBox.current && !DEBUG) {
      if ('serviceWorker' in navigator) {
        try {
          workBox.current = new Workbox(DEFAULT_URI);
          if (workBox.current) {
            workBox.current.addEventListener('waiting', () => {
              setIsUpdateAvailable(true);
            });
            workBox.current.register();
          }
        } catch (err) {
          console.error('Failed to register service worker with workbox.', err);
        }
      } else {
        console.warn('Service worker API not available. Cannot install service worker.');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Define the skipWaiting callback. Used to transition an 'installed' service worker to 'active'
  const updateApp = useCallback(() => {
    if (!workBox.current) return;
    setIsUpdateInProgress(true);
    workBox.current.addEventListener('controlling', () => {
      window.location.reload();
    });
    workBox.current.messageSkipWaiting();
  }, [workBox]);

  // Used to Call 'update' on active service worker
  const checkForServiceWorkerUpdate = useCallback(() => {
    if (workBox.current) workBox.current.update();
  }, []);

  // Check for service worker updates when our URL changes
  const location = useLocation();
  useEffect(() => {
    checkForServiceWorkerUpdate();
  }, [location, checkForServiceWorkerUpdate]);

  return {
    isUpdateAvailable,
    isUpdateInProgress,
    updateApp,
    checkForServiceWorkerUpdate
  };
};

// From https://stackoverflow.com/a/67710693
export const ServiceWorkerContext = createContext({} as ReturnType<typeof useServiceWorker>);

export const ServiceWorkerContextProvider = (props: { children?: React.ReactNode }): React.ReactElement => {
  return <ServiceWorkerContext value={useServiceWorker()}>{props.children}</ServiceWorkerContext>;
};
